import { useEffect, useRef, useState } from "react";

/**
 * Custom hook to manage asynchronous loading of child nodes.
 */
export const useAsyncLoadChildren = () => {
  const asyncNodeList = useRef(new Map<number, Promise<void>>());
  const [loading, setLoading] = useState(false);

  /**
   * Updates the loading queue with a new promise.
   */
  const updateLoadingQueue = (index: number, promise: Promise<void>) => {
    const queue = asyncNodeList.current.set(index, promise);
    setLoading(true);
    return queue;
  };

  useEffect(() => {
    if (loading) {
      Promise.all(Array.from(asyncNodeList.current.values())).then(() => {
        setLoading(false);
      });
    }
  }, [loading]);

  /**
   * Memoizes the loading state for a given node.
   */
  const memoizeLoading = (index: number, promiseGetter: (index: number) => Promise<void>) =>
    asyncNodeList.current.get(index) ?? updateLoadingQueue(index, promiseGetter(index)).get(index) ?? [];

  return { memoizeLoading, loading };
};
